import {chain} from 'lodash';
import {connect} from 'react-redux';

import {
  BulkActionsTrackingTypesEnum,
  CellsActionsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {getTimeFormat} from '../../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {makeGetWillShowTriageForTeamAlert} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionSelectors';
import {
  findConversationsCanceledReminder,
  findConversationsReminder,
  makeFindConversationActionContext,
} from '../../../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {AppState} from '../../../../../../store/appState';
import {
  bulkSetConversationsReminderAction,
  setConversationsReminderAction,
} from '../../../../../../store/data/conversations/reminderConversationsActions';
import {
  getCustomSnoozes,
  getReminderPresets,
  getReminderValues,
} from '../../../../../../store/data/preferences/preferencesSelectors';
import {getFindTeammate, getUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {navigateToSnoozesAction} from '../../../../../../store/stateless/navigationActions';
import {SnoozeDropdown} from './snoozeDropdown';

/*
 * Props.
 */

interface SnoozeDropdownContainerProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  // Inboxes parameters are only applicable to bulk actions and are optional for P2 quick actions.
  inboxesParameters?: InboxesParameters;
  isBulkActionRequired: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  isSnoozeFromCellHoverIcon?: boolean;
}

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: SnoozeDropdownContainerProps) {
  return props.conversations;
}

function getCellsContext(_state: AppState, props: SnoozeDropdownContainerProps) {
  return props.cellsContext;
}

function makeGetAssignees() {
  return createSelector([getConversations, getFindTeammate], (conversations, findTeammate) =>
    chain(conversations)
      .map((c) => (c.assigneeId && findTeammate(c.assigneeId)) || undefined)
      .uniq()
      .value(),
  );
}

function makeGetValue(getReminder: ReturnType<typeof makeGetReminder>) {
  return createSelector([getReminder, getFindTeammate, getUser], (reminder, findTeammate, user) => {
    if (!reminder) {
      return undefined;
    }

    const teammate = findTeammate(reminder.teammateId);
    if (!teammate) {
      throw new Error('The teammate for this reminder could not be found.');
    }

    return {
      remindAt: reminder.remindAt,
      isTeammateUser: teammate.id === user.id,
      teammate,
    };
  });
}

/** Find a common reminder for our user in all the conversations. */
function makeGetReminder() {
  return createSelector([getConversations, getCellsContext], (conversations, cellsContext) =>
    findConversationsReminder(cellsContext, conversations),
  );
}

/** Find a common canceled reminder for our user in all the conversations. */
function makeGetCanceledReminder() {
  return createSelector([getConversations, getCellsContext], (conversations, cellsContext) =>
    findConversationsCanceledReminder(cellsContext, conversations),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SnoozeDropdownContainerProps>()(() => {
  const getReminder = makeGetReminder();

  return {
    values: getReminderValues,
    reminderPresets: getReminderPresets,
    customSnoozes: getCustomSnoozes,
    value: makeGetValue(getReminder),
    assignees: makeGetAssignees(),
    timeFormat: getTimeFormat,
    reminder: getReminder,
    canceledReminder: makeGetCanceledReminder(),
    shouldShowTeamSnoozeAlert: makeGetWillShowTriageForTeamAlert(
      getCellsContext,
      getConversations,
      TriageActionsEnum.SNOOZE,
    ),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToSnoozesAction,
  setConversationReminder: setConversationsReminderAction,
  bulkSetConversationsReminder: bulkSetConversationsReminderAction,
}));

export const SnoozeDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {inboxesParameters, conversations, cellsContext, onInterfaceEvent, isSnoozeFromCellHoverIcon} =
      ownProps;
    const conversationIds = conversations.map((c) => c.id);

    // Build the action to snooze the conversation.
    const onSnooze: (arg: number) => void = (remindAt) => {
      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.SNOOZE, false);
      }

      if (isSnoozeFromCellHoverIcon) {
        trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
          action: CellsActionsEnum.SNOOZE,
        });
      }

      dispatchProps.setConversationReminder(
        cellsContext,
        conversationIds,
        remindAt,
        makeFindConversationActionContext(cellsContext, conversations),
      );
    };

    // Build the action to unsnooze the conversation.
    const onUnsnooze = () => {
      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.UNSNOOZE, false);
      }

      dispatchProps.setConversationReminder(
        cellsContext,
        conversationIds,
        null,
        makeFindConversationActionContext(cellsContext, conversations),
      );
    };

    const onBulkSnooze: (arg: number) => void = (remindAt) => {
      if (!inboxesParameters) {
        return;
      }

      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.SNOOZE, true);
      }

      dispatchProps.bulkSetConversationsReminder(inboxesParameters, cellsContext, remindAt);
    };

    // Build the action to bulk unsnooze the conversation.
    const onBulkUnsnooze = () => {
      if (!inboxesParameters) {
        return;
      }

      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.UNSNOOZE, true);
      }

      dispatchProps.bulkSetConversationsReminder(inboxesParameters, cellsContext, null);
    };

    const onOpenSnoozesClick = () => {
      dispatchProps.navigateToLink();
    };

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onSnooze,
      onUnsnooze,
      onBulkSnooze,
      onBulkUnsnooze,
      onOpenSnoozesClick,
    };
  },
)(SnoozeDropdown);
